import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Divider from 'components/Luxkit/Divider'
import LineChairWithAirplaneIcon from 'components/Luxkit/Icons/line/LineChairWithAirplaneIcon'
import LineShieldCheckIcon from 'components/Luxkit/Icons/line/LineShieldCheckIcon'
import LineTaxiIcon from 'components/Luxkit/Icons/line/LineTaxiIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  /** To see the effect of the border-radius */
  overflow: hidden;
`

const CustomDivider = styled(Divider)`
  && {
    background-color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }
`

interface Props {
  onLinkClick: () => void
}

function AccountMenuLoyaltyNoAccountSilverTile({ onLinkClick }: Props) {
  const dispatch = useAppDispatch()

  const handleActivateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    onLinkClick()
  }, [dispatch, onLinkClick])

  return <Container to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLinkClick}>
    <VerticalSpacer gap={20}>
      <VerticalSpacer gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LoyaltyProgramNameLogo width={72} />
          <BodyText variant="medium" format="uppercase">Silver</BodyText>
        </Group>
        <LoyaltyTierLabel
          tier="silver"
          kind="primary"
          format="uppercase"
        >
          EXCLUSIVE INVITE
        </LoyaltyTierLabel>
        <Heading variant="heading4">
          You've unlocked{' '}
          <Colour value="tier-silver-light-contrast">Silver status</Colour>
        </Heading>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        <BodyText variant="small" weight="bold">
          Your benefits, ready when you are
        </BodyText>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyTextBlock variant="small" startIcon={<LineChairWithAirplaneIcon />}>
            Free airport lounge passes
          </BodyTextBlock>
          <LoyaltyTierLabel kind="tertiary" tier="silver">
            1x
          </LoyaltyTierLabel>
        </Group>
        <CustomDivider kind="primary" />
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyTextBlock variant="small" startIcon={<LineTaxiIcon />}>
            Tour add-on credits
          </BodyTextBlock>
          <LoyaltyTierLabel kind="tertiary" tier="silver">
            2x <FormatCurrency value={100} format="dynamic" />
          </LoyaltyTierLabel>
        </Group>
        <CustomDivider kind="primary" />
        <BodyTextBlock variant="small" startIcon={<LineShieldCheckIcon />}>
          Discounted travel insurance
        </BodyTextBlock>
      </VerticalSpacer>
      <TextButton kind="primary" size="medium" fit="full-width" onClick={handleActivateMembership}>
        Activate free membership
      </TextButton>
    </VerticalSpacer>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyNoAccountSilverTile)
